import {
  AfterContentInit,
  Directive,
  ElementRef,
  OnChanges,
  SimpleChanges,
  inject,
  input,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { AppIconRegistry } from './app-icon.registry';

/** Selecciona automáticamente entre un SVG propio y un icono de fuente. */
@Directive({
  selector: 'mat-icon[appIcon]',
  standalone: true,
})
export class AppIconDirective implements AfterContentInit, OnChanges {
  readonly appIcon = input<string>('');

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly matIcon = inject(MatIcon);
  private readonly iconRegistry = inject(AppIconRegistry);

  /** Actualiza el icono cuando cambia un valor enlazado mediante `[appIcon]`. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appIcon'] && this.appIcon().trim()) {
      this.render(this.appIcon());
    }
  }

  /** Obtiene una única vez el nombre estático escrito como contenido del elemento. */
  ngAfterContentInit(): void {
    // El atributo `appIcon` sin valor admite texto estático. Angular no expone
    // el texto interpolado como input, por lo que los nombres dinámicos deben
    // enlazarse explícitamente mediante `[appIcon]`.
    if (!this.appIcon().trim()) {
      this.render(this.elementRef.nativeElement.textContent ?? '');
    }
  }

  private render(value: string): void {
    const name = value.trim();

    /*
     * MatIcon admite tres mecanismos de representación diferentes:
     * - `svgIcon` para SVG registrados:
     *   `<mat-icon svgIcon="customer"></mat-icon>`.
     * - `fontIcon` junto con `fontSet` para fuentes basadas en clases CSS:
     *   `<mat-icon fontSet="fa" fontIcon="fa-home"></mat-icon>`.
     * - El contenido textual del elemento para fuentes basadas en ligaduras:
     *   `<mat-icon>home</mat-icon>`.
     *
     * Los iconos propios de la aplicación utilizan el primer mecanismo y los
     * Material Symbols del proyecto utilizan ligaduras. Por ello, un nombre que
     * no pertenezca al catálogo SVG debe escribirse en `textContent`, como ocurre
     * en `<mat-icon>home</mat-icon>`, y no asignarse a `fontIcon`: hacerlo lo
     * trataría como una clase CSS. Cada rama limpia los otros mecanismos para
     * evitar que un cambio dinámico conserve el SVG, la clase o la ligadura del
     * icono representado anteriormente.
     */
    if (!name) {
      this.matIcon.svgIcon = '';
      this.matIcon.fontIcon = '';
      this.elementRef.nativeElement.textContent = '';
      return;
    }

    if (this.iconRegistry.ensureRegistered(name)) {
      this.elementRef.nativeElement.textContent = '';
      this.matIcon.fontIcon = '';
      this.matIcon.svgIcon = name;
      return;
    }

    this.matIcon.svgIcon = '';
    this.matIcon.fontIcon = '';
    this.elementRef.nativeElement.textContent = name;
  }
}
